import { Building2, Calendar, Clock, Filter, User, Users, MoreHorizontal, ToggleLeft, ToggleRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import ActionMenu from "../../../components/shared/ActionMenu";
import DataTable from "../../../components/shared/DataTable";
import LazySearchSelect from "../../../components/shared/LazySearchSelect";
import StatusBadge from "../../../components/shared/StatusBadge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch.jsx";
import { formatDate } from "../../../utils/dateUtils";

const AttendanceTable = ({
  attendance,
  filters,
  onFilterChange,
  permissions,
  onDelete,
  sortBy,
  direction,
  onSort,
  showDeleted,
  setShowDeleted,
  setCurrentPage
}) => {
  const navigate = useNavigate();

  const attendanceColumns = [
    {
      header: "Worker",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters.selectedUser !== "ALL" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Worker</p>
              <LazySearchSelect
                value={filters.selectedUser}
                onChange={(val) => onFilterChange("user", val)}
                apiUrl="/users"
                dataKey="items"
                labelKey="name"
                valueKey="id"
                placeholder="Select worker"
                searchPlaceholder="Search workers..."
                showAllOption={true}
                allOptionLabel="All Workers"
                allOptionValue="ALL"
              />
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "workerName",
      isPrimary: true,
      render: (val, record) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 dark:bg-yale-blue-800 rounded-full shrink-0">
            <User className="w-4 h-4 text-yale-blue-500" />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-sm font-semibold text-yale-blue-900 dark:text-slate-100 whitespace-normal break-words">
                {record.assignedUser?.name || record.workerName}
              </span>
              {record.deleted && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
                  Deleted
                </span>
              )}
            </div>
            {record.notes && <span className="text-[10px] text-slate-500 italic truncate max-w-[150px]">{record.notes}</span>}
          </div>
        </div>
      )
    },
    {
      header: "Type",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters.selectedType !== "ALL" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-4" align="start">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Type</p>
              <Select value={filters.selectedType} onValueChange={(val) => onFilterChange("type", val)}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Types</SelectItem>
                  <SelectItem value="OFFICE">Office</SelectItem>
                  <SelectItem value="FIELD">Field</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "type",
      render: (val) => (
        <div className="flex items-center gap-1.5">
          <StatusBadge status={val === "OFFICE" ? "OFFICE" : "FIELD"} variant={val === "OFFICE" ? "secondary" : "outline"} />
        </div>
      )
    },
    {
      header: "Project",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters.selectedProject !== "ALL" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Project</p>
              <LazySearchSelect
                value={filters.selectedProject}
                onChange={(val) => onFilterChange("project", val)}
                apiUrl="/projects"
                labelKey="name"
                placeholder="Select project"
                searchPlaceholder="Search projects..."
                showAllOption={true}
                allOptionLabel="All Projects"
                allOptionValue="ALL"
              />
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "projectId",
      render: (val, record) => {
        const isOffice = record.type === "OFFICE";
        const project = record.project;

        if (isOffice) {
          return (
            <div className="flex items-center gap-1.5 text-slate-400 italic text-xs">
              <Building2 className="w-3.5 h-3.5 shrink-0 opacity-50" />
              <span>N/A (Global)</span>
            </div>
          );
        }

        return (
          <div
            className="text-sm text-yale-blue-600 dark:text-yale-blue-400 hover:text-yale-blue-700 dark:hover:text-yale-blue-300 cursor-pointer font-medium flex items-center gap-1.5 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              if (record.project?.id || record.projectId) {
                navigate(`/projects/${record.project?.id || record.projectId}`);
              }
            }}
          >
            <Building2 className="w-3.5 h-3.5 shrink-0" />
            <span>{project?.name || "N/A"}</span>
          </div>
        );
      }
    },
    {
      header: "Date",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${filters.dateRange.start || filters.dateRange.end ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Date Range</p>
              <div className="space-y-2">
                <Label htmlFor="start-date" className="text-[10px]">
                  Start Date
                </Label>
                <Input
                  id="start-date"
                  type="date"
                  value={filters.dateRange.start ? new Date(filters.dateRange.start).toISOString().split("T")[0] : ""}
                  onChange={(e) =>
                    onFilterChange("dateRange", {
                      ...filters.dateRange,
                      start: e.target.value ? new Date(e.target.value) : null
                    })
                  }
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date" className="text-[10px]">
                  End Date
                </Label>
                <Input
                  id="end-date"
                  type="date"
                  value={filters.dateRange.end ? new Date(filters.dateRange.end).toISOString().split("T")[0] : ""}
                  min={filters.dateRange.start ? new Date(filters.dateRange.start).toISOString().split("T")[0] : ""}
                  disabled={!filters.dateRange.start}
                  onChange={(e) =>
                    onFilterChange("dateRange", {
                      ...filters.dateRange,
                      end: e.target.value ? new Date(e.target.value) : null
                    })
                  }
                  className="h-8 text-xs"
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => onFilterChange("dateRange", { start: null, end: null })}
                >
                  Clear Range
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "date",
      sortable: true,
      sortKey: "date",
      render: (val) => (
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
          <Calendar className="w-3.5 h-3.5 text-yale-blue-500" />
          {val ? formatDate(val) : "N/A"}
        </div>
      )
    },
    {
      header: "Hours",
      accessor: "hoursWorked",
      render: (val) => (
        <div className="flex items-center gap-1.5 text-sm font-semibold text-yale-blue-700 dark:text-yale-blue-400">
          <Clock className="w-3.5 h-3.5" />
          {val} hrs
        </div>
      )
    },
    {
      header: "Status",
      accessor: "status",
      render: (val) => <StatusBadge status={val} />
    },
    {
      header: "Actions",
      filter: permissions?.canDelete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setCurrentPage(0);
                }}
                id="show-deleted-attendance-table"
              />
              <Label htmlFor="show-deleted-attendance-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, record) => (
        <ActionMenu canEdit={false} canDelete={permissions?.canDelete && !record.deleted} onDelete={() => onDelete(record.id)} />
      )
    }
  ];

  return (
    <DataTable
      columns={attendanceColumns}
      data={attendance}
      emptyStateTitle="No attendance records"
      emptyStateDescription="Start tracking team attendance by clicking 'Record Attendance'."
      emptyStateIcon={Users}
      sortBy={sortBy}
      direction={direction}
      onSort={onSort}
    />
  );
};

export default AttendanceTable;
